/**
 * 枪皮相关 Store
 * 负责枪皮数据的加载、管理和缓存
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { cacheKeys } from '../config'
import { gunSkinsApi } from '@/https/api/gunSkins'
import type { GunSkin, GunSkinCreateRequest, GunSkinUpdateRequest } from '@/https/types/gunSkins'

export const useGunSkinsStore = defineStore('gunSkins', () => {
    const cacheStore = useCacheStore()

    // ==================== 状态管理 ====================

    // 枪皮列表
    const gunSkinList = ref<GunSkin[]>([])

    // 加载状态
    const gunSkinListLoading = ref(false)

    // 操作加载状态
    const gunSkinOperationLoading = ref(false)

    // 错误信息
    const gunSkinListError = ref<string | null>(null)

    // ==================== 计算属性 ====================

    // 是否有枪皮数据
    const hasGunSkinList = computed(() => gunSkinList.value.length > 0)

    // 枪皮总数
    const gunSkinCount = computed(() => gunSkinList.value.length)

    // 按排序权重排序的枪皮列表
    const sortedGunSkinList = computed(() => {
        return [...gunSkinList.value].sort((a, b) => {
            // 排序权重小的排在前面，没有权重的排在后面
            const orderA = a.gun_skin_order ?? 999999
            const orderB = b.gun_skin_order ?? 999999
            return orderA - orderB
        })
    })

    // 枪皮选项（用于下拉选择）
    const gunSkinOptions = computed(() => {
        return sortedGunSkinList.value.map(gunSkin => ({
            label: gunSkin.gun_skin_name,
            value: gunSkin.id
        }))
    })

    // ==================== 数据加载 ====================

    /**
     * 加载所有枪皮数据
     * @param forceRefresh 是否强制刷新
     */
    const loadGunSkinList = async (forceRefresh = false): Promise<GunSkin[]> => {
        try {
            gunSkinListLoading.value = true
            gunSkinListError.value = null

            const response = await cacheStore.smartCache(
                cacheKeys.config.gunSkins,
                () => {
                    return gunSkinsApi.getAllGunSkins({ page: 1, size: 100 })
                },
                forceRefresh
            )
            // 处理分页响应格式：提取 items 字段
            let gunSkinsArray: GunSkin[] = []
            if (Array.isArray(response)) {
                // 如果直接是数组（未分页）
                gunSkinsArray = response
            } else if (response && typeof response === 'object' && 'items' in response) {
                // 如果是分页格式，提取 items
                gunSkinsArray = Array.isArray(response.items) ? response.items : []
            }

            gunSkinList.value = gunSkinsArray
            return gunSkinList.value
        } catch (error) {
            console.error('❌ [GunSkinStore] 加载枪皮数据失败:', error)
            gunSkinListError.value = error instanceof Error ? error.message : '加载枪皮数据失败'
            return []
        } finally {
            gunSkinListLoading.value = false
        }
    }

    const setGunSkinList = (items: GunSkin[]) => {
        gunSkinList.value = Array.isArray(items) ? items : []
        gunSkinListError.value = null
    }

    /**
     * 刷新枪皮数据
     */
    const refreshGunSkinList = async (): Promise<GunSkin[]> => {
        return await loadGunSkinList(true)
    }

    // ==================== 数据操作（管理员功能） ====================

    /**
     * 创建枪皮
     * @param gunSkinData 枪皮数据
     */
    const createGunSkin = async (gunSkinData: GunSkinCreateRequest): Promise<boolean> => {
        try {
            gunSkinOperationLoading.value = true
            gunSkinListError.value = null

            await gunSkinsApi.createGunSkin(gunSkinData)

            // 重新加载枪皮列表
            await loadGunSkinList(true)

            return true
        } catch (error) {
            console.error('创建枪皮失败:', error)
            gunSkinListError.value = error instanceof Error ? error.message : '创建枪皮失败'
            return false
        } finally {
            gunSkinOperationLoading.value = false
        }
    }

    /**
     * 更新枪皮
     * @param id 枪皮ID
     * @param gunSkinData 枪皮数据
     */
    const updateGunSkin = async (id: number, gunSkinData: GunSkinUpdateRequest): Promise<boolean> => {
        try {
            gunSkinOperationLoading.value = true
            gunSkinListError.value = null

            await gunSkinsApi.updateGunSkin(id, gunSkinData)

            // 重新加载枪皮列表
            await loadGunSkinList(true)

            return true
        } catch (error) {
            console.error('更新枪皮失败:', error)
            gunSkinListError.value = error instanceof Error ? error.message : '更新枪皮失败'
            return false
        } finally {
            gunSkinOperationLoading.value = false
        }
    }

    /**
     * 删除枪皮
     * @param id 枪皮ID
     */
    const deleteGunSkin = async (id: number): Promise<boolean> => {
        try {
            gunSkinOperationLoading.value = true
            gunSkinListError.value = null

            await gunSkinsApi.deleteGunSkin(id)

            // 重新加载枪皮列表
            await loadGunSkinList(true)

            return true
        } catch (error) {
            console.error('删除枪皮失败:', error)
            gunSkinListError.value = error instanceof Error ? error.message : '删除枪皮失败'
            return false
        } finally {
            gunSkinOperationLoading.value = false
        }
    }

    // ==================== 工具方法 ====================

    /**
     * 根据ID查找枪皮
     * @param id 枪皮ID
     */
    const findGunSkinById = (id: number): GunSkin | null => {
        return gunSkinList.value.find(gunSkin => gunSkin.id === id) || null
    }

    /**
     * 根据名称查找枪皮
     * @param name 枪皮名称
     */
    const findGunSkinByName = (name: string): GunSkin | null => {
        return gunSkinList.value.find(gunSkin => gunSkin.gun_skin_name === name) || null
    }

    /**
     * 清除错误信息
     */
    const clearGunSkinListError = (): void => {
        gunSkinListError.value = null
    }

    // ==================== 初始化 ====================

    /**
     * 初始化枪皮数据
     */
    const initGunSkins = async (): Promise<void> => {
        try {
            await loadGunSkinList()
        } catch (error) {
            console.error('初始化枪皮数据失败:', error)
        }
    }

    // ==================== 导出 ====================

    return {
        // 状态 - 直接返回ref，保持响应式
        gunSkinList,
        gunSkinListLoading,
        gunSkinOperationLoading,
        gunSkinListError,

        // 计算属性
        hasGunSkinList,
        gunSkinCount,
        sortedGunSkinList,
        gunSkinOptions,

        // 数据加载
        loadGunSkinList,
        setGunSkinList,
        refreshGunSkinList,

        // 数据操作
        createGunSkin,
        updateGunSkin,
        deleteGunSkin,

        // 工具方法
        findGunSkinById,
        findGunSkinByName,
        clearGunSkinListError,

        // 初始化
        initGunSkins
    }
})
